import React, { useMemo } from 'react';
import { Breadcrumb as AntBreadcrumb } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import type { ManagerDetailResponseTreeItem } from '@/apis';
import { renderIcon } from '@/utils/iconRenderer';
import styles from './index.module.less';

interface BreadcrumbProps {
  permissionTree: ManagerDetailResponseTreeItem[];
}

interface BreadcrumbItem {
  title: string;
  path?: string;
  icon?: React.ReactNode;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ permissionTree }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // 将权限树扁平化，方便查找
  const flattenPermissionTree = useMemo(() => {
    const flatten = (
      items: ManagerDetailResponseTreeItem[],
      result: ManagerDetailResponseTreeItem[] = []
    ): ManagerDetailResponseTreeItem[] => {
      items.forEach(item => {
        result.push(item);
        if (item.children && item.children.length > 0) {
          flatten(item.children, result);
        }
      });
      return result;
    };
    return flatten(permissionTree);
  }, [permissionTree]);

  // 根据当前路径生成面包屑
  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbItem[] = [];
    const currentPath = location.pathname;

    // 添加首页
    items.push({
      title: '首页',
      path: '/',
      icon: <HomeOutlined />,
    });

    // 如果不是首页，则根据路径生成面包屑
    if (currentPath !== '/') {
      // 根据当前路径查找对应的权限项
      const currentPermission = flattenPermissionTree.find(
        item => item.permissionMark === currentPath
      );

      if (currentPermission) {
        // 构建面包屑路径
        const buildBreadcrumbPath = (
          permission: ManagerDetailResponseTreeItem,
          path: BreadcrumbItem[] = []
        ): BreadcrumbItem[] => {
          // 添加当前项
          path.unshift({
            title: permission.permissionName,
            path: permission.permissionMark || undefined,
            icon: renderIcon(permission.icon, permission.permissionMark),
          });

          // 查找父级权限
          if (permission.parentKey) {
            const parentPermission = flattenPermissionTree.find(
              item => item.permissionKey === permission.parentKey
            );
            if (parentPermission) {
              return buildBreadcrumbPath(parentPermission, path);
            }
          }

          return path;
        };

        const breadcrumbPath = buildBreadcrumbPath(currentPermission);
        items.push(...breadcrumbPath);
      } else {
        // 如果没有找到对应的权限项，根据路径手动构建
        const pathSegments = currentPath.split('/').filter(Boolean);
        let currentPathStr = '';

        // 产品编辑 /cms/product/edit/:id 最后显示为「编辑」；产品创建 /cms/product/create 最后显示为「创建」
        const isProductEdit = /^\/cms\/product\/edit\/[^/]+$/.test(currentPath);
        const isProductCreate = currentPath === '/cms/product/create';
        const segmentCount = pathSegments.length;
        const limit = isProductEdit ? segmentCount - 2 : isProductCreate ? segmentCount - 1 : segmentCount;

        pathSegments.forEach((segment, i) => {
          currentPathStr += `/${segment}`;

          if (isProductEdit && i >= limit) return;
          if (isProductCreate && i >= limit) return;

          // 尝试从权限树中找到匹配的项
          const matchedPermission = flattenPermissionTree.find(
            item => item.permissionMark === currentPathStr
          );

          if (matchedPermission) {
            items.push({
              title: matchedPermission.permissionName,
              path: matchedPermission.permissionMark || undefined,
              icon: renderIcon(matchedPermission.icon, matchedPermission.permissionMark),
            });
          } else {
            // 如果没有找到匹配的权限，使用路径段作为标题
            const title =
              segment === 'system'
                ? '系统管理'
                : segment === 'manager'
                  ? '用户管理'
                  : segment === 'managerRole'
                    ? '角色管理'
                    : segment === 'managerPermission'
                      ? '权限管理'
                      : segment;

            items.push({
              title,
              path: currentPathStr,
            });
          }
        });

        if (isProductEdit) {
          items.push({ title: '编辑' });
        } else if (isProductCreate) {
          items.push({ title: '创建' });
        }
      }
    }

    return items;
  }, [location.pathname, flattenPermissionTree]);

  // 处理面包屑点击
  const handleBreadcrumbClick = (path?: string) => {
    if (path && path !== location.pathname) {
      navigate(path);
    }
  };

  return (
    <div className={styles.breadcrumbContainer}>
      <AntBreadcrumb>
        {breadcrumbItems.map((item, index) => (
          <AntBreadcrumb.Item key={index}>
            {index === breadcrumbItems.length - 1 || !item.path ? (
              // 最后一项或没有路径的项不可点击
              <span className={styles.currentItem}>
                {item.icon && (
                  <span className={styles.iconWrapper}>{item.icon}</span>
                )}
                <span className={styles.textWrapper}>{item.title}</span>
              </span>
            ) : (
              // 其他项可点击
              <span
                className={styles.clickableItem}
                onClick={() => handleBreadcrumbClick(item.path)}
              >
                {item.icon && (
                  <span className={styles.iconWrapper}>{item.icon}</span>
                )}
                <span className={styles.textWrapper}>{item.title}</span>
              </span>
            )}
          </AntBreadcrumb.Item>
        ))}
      </AntBreadcrumb>
    </div>
  );
};

export default Breadcrumb;
